/**
 * Role-based access control for OrbitFlow.
 *
 * Four roles, in descending order of privilege:
 *   admin            — full control: users, roles, projects, issues, sprints, time
 *   project_manager  — projects (CRUD) + members, sprints, all issues, time
 *   manager          — all issues + assignment, sprints, time
 *   developer        — create/edit own issues, assign to self, log own time
 *
 * Pure functions only — safe to import on both client and server.
 */

export type Role = 'admin' | 'project_manager' | 'manager' | 'developer'

export const ROLES: Role[] = ['admin', 'project_manager', 'manager', 'developer']

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin',
  project_manager: 'Project Manager',
  manager: 'Manager',
  developer: 'Developer',
}

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  admin: 'Full access — manages users, roles, projects, and all work.',
  project_manager: 'Creates and runs projects, sprints, and team membership.',
  manager: 'Manages all issues and sprints, assigns work, tracks time.',
  developer: 'Works on issues, assigns to self, and logs own time.',
}

export type Permission =
  | 'users.manage'          // invite / create / deactivate users
  | 'users.assign_roles'    // change a user's role
  | 'projects.create'
  | 'projects.edit'
  | 'projects.delete'
  | 'projects.manage_members'
  | 'sprints.manage'        // create / edit / delete sprints
  | 'issues.create'
  | 'issues.edit_any'       // edit any issue in the org
  | 'issues.edit_own'       // edit issues authored by / assigned to self
  | 'issues.delete'
  | 'issues.assign_any'     // assign issues to anyone
  | 'issues.assign_self'    // assign issues to self only
  | 'time.log_own'
  | 'time.view_all'

const MANAGER_ISSUE: Permission[] = [
  'issues.create', 'issues.edit_any', 'issues.edit_own', 'issues.delete', 'issues.assign_any', 'issues.assign_self',
]

const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  admin: [
    'users.manage', 'users.assign_roles',
    'projects.create', 'projects.edit', 'projects.delete', 'projects.manage_members',
    'sprints.manage',
    ...MANAGER_ISSUE,
    'time.log_own', 'time.view_all',
  ],
  project_manager: [
    'projects.create', 'projects.edit', 'projects.delete', 'projects.manage_members',
    'sprints.manage',
    ...MANAGER_ISSUE,
    'time.log_own', 'time.view_all',
  ],
  manager: [
    'sprints.manage',
    ...MANAGER_ISSUE,
    'time.log_own', 'time.view_all',
  ],
  developer: [
    'issues.create', 'issues.edit_own', 'issues.assign_self',
    'time.log_own',
  ],
}

export function normalizeRole(role: string | null | undefined): Role {
  return ROLES.includes(role as Role) ? (role as Role) : 'developer'
}

/** Does this role hold the given permission? */
export function can(role: string | null | undefined, permission: Permission): boolean {
  return ROLE_PERMISSIONS[normalizeRole(role)].includes(permission)
}

/** Does this role hold every one of the given permissions? */
export function canAll(role: string | null | undefined, permissions: Permission[]): boolean {
  return permissions.every((p) => can(role, p))
}

/** Does this role hold at least one of the given permissions? */
export function canAny(role: string | null | undefined, permissions: Permission[]): boolean {
  return permissions.some((p) => can(role, p))
}

/** All permissions granted to a role — handy for debugging / UI. */
export function permissionsFor(role: string | null | undefined): Permission[] {
  return [...ROLE_PERMISSIONS[normalizeRole(role)]]
}

/**
 * Can this role assign an issue to `targetUserId`?
 * Managers and above assign freely; developers may only assign to themselves.
 */
export function canAssignIssueTo(
  role: string | null | undefined,
  actorUserId: string | null | undefined,
  targetUserId: string | null | undefined,
): boolean {
  if (can(role, 'issues.assign_any')) return true
  if (can(role, 'issues.assign_self')) return !targetUserId || targetUserId === actorUserId
  return false
}
